import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import BusinessLogFilters from './BusinessLogFilters';
import { DEFAULT_ACTION_OPTIONS, INITIAL_FILTERS, makeQuickFilterFilters } from './businessLogFilterConfig';
import {
  tableWrapStyle, tblStyle, thStyle, trStyle, tdStyle,
  placeholderStyle, paginationStyle, pageBtnStyle,
  mobileLogListStyle, mobileLogCardStyle, mobileCardTopStyle,
  mobileCardNameStyle, mobileCardSubStyle, mobileCardTimeStyle,
} from './styles';

const LIMIT = 50;

function actionBadgeStyle(action) {
  const palette = {
    create: ['rgba(34,197,94,0.12)', '#15803d'],
    update: ['rgba(99,102,241,0.12)', '#4f46e5'],
    delete: ['rgba(239,68,68,0.12)', '#dc2626'],
    cancel: ['rgba(239,68,68,0.12)', '#dc2626'],
    restore: ['rgba(20,184,166,0.12)', '#0f766e'],
    confirm: ['rgba(14,165,233,0.12)', '#0369a1'],
    reopen: ['rgba(245,158,11,0.14)', '#b45309'],
    grant: ['rgba(34,197,94,0.12)', '#15803d'],
    revoke: ['rgba(239,68,68,0.12)', '#dc2626'],
    upload: ['rgba(14,165,233,0.12)', '#0369a1'],
    apply: ['rgba(34,197,94,0.12)', '#15803d'],
    reset_password: ['rgba(245,158,11,0.14)', '#b45309'],
    step_changed: ['rgba(99,102,241,0.12)', '#4f46e5'],
    shipping_updated: ['rgba(14,165,233,0.12)', '#0369a1'],
    keep_update: ['rgba(20,184,166,0.12)', '#0f766e'],
    impersonate: ['rgba(245,158,11,0.14)', '#b45309'],
  };
  const [background, color] = palette[action] || ['var(--bg-subtle)', 'var(--text-sub)'];
  return {
    display: 'inline-flex',
    alignItems: 'center',
    minWidth: 52,
    justifyContent: 'center',
    padding: '3px 8px',
    borderRadius: 7,
    background,
    color,
    fontSize: 11,
    fontWeight: 800,
    whiteSpace: 'nowrap',
  };
}

function DetailPanel({ row }) {
  const details = row.detail_items || [];
  return (
    <div style={{
      marginTop: 8,
      padding: '10px 12px',
      borderRadius: 8,
      background: 'var(--bg-subtle)',
      border: '1px solid var(--border)',
      display: 'grid',
      gap: 7,
    }}>
      <div style={{ color: 'var(--text)', fontSize: 12, fontWeight: 700 }}>{row.summary}</div>
      {details.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>상세 변경값이 기록되지 않았습니다.</div>
      ) : details.map((item, idx) => (
        <div key={`${item.label}-${idx}`} style={{
          display: 'grid',
          gridTemplateColumns: '120px minmax(0, 1fr)',
          gap: 8,
          fontSize: 12,
          alignItems: 'start',
        }}>
          <div style={{ color: 'var(--text-muted)', fontWeight: 700 }}>{item.label}</div>
          <div style={{ color: 'var(--text-sub)', wordBreak: 'break-word' }}>
            <span style={{ color: 'var(--text-muted)' }}>{item.before || '-'}</span>
            <span style={{ margin: '0 8px', color: 'var(--text-muted)' }}>→</span>
            <span style={{ color: 'var(--text)', fontWeight: 700 }}>{item.after || '-'}</span>
          </div>
        </div>
      ))}
      <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>
        원본: {row.source_table || row.source} #{row.raw_id}
      </div>
    </div>
  );
}

function MobileCard({ row, expanded, onToggle }) {
  return (
    <div style={mobileLogCardStyle} onClick={onToggle} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onToggle()}>
      <div style={mobileCardTopStyle}>
        <div>
          <div style={{ ...mobileCardNameStyle, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={actionBadgeStyle(row.action)}>{row.action_label}</span>
            <span>{row.menu}</span>
          </div>
          <div style={{ ...mobileCardSubStyle, marginTop: 5 }}>{row.target}</div>
          <div style={{ ...mobileCardSubStyle, marginTop: 3 }}>처리자: {row.actor}</div>
        </div>
        <span style={mobileCardTimeStyle}>{row.created_at_kst?.slice(0, 16)}</span>
      </div>
      {expanded && <DetailPanel row={row} />}
    </div>
  );
}

function Pagination({ page, totalPages, onMove }) {
  if (totalPages <= 1) return null;
  let start = Math.max(1, page - 3);
  const end = Math.min(totalPages, start + 6);
  start = Math.max(1, end - 6);
  return (
    <div style={paginationStyle}>
      <button style={pageBtnStyle(false)} disabled={page <= 1} onClick={() => onMove(1)}>처음</button>
      <button style={pageBtnStyle(false)} disabled={page <= 1} onClick={() => onMove(page - 1)}>이전</button>
      {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
        const p = start + i;
        if (p > totalPages) return null;
        return <button key={p} style={pageBtnStyle(p === page)} onClick={() => onMove(p)}>{p}</button>;
      })}
      <button style={pageBtnStyle(false)} disabled={page >= totalPages} onClick={() => onMove(page + 1)}>다음</button>
      <button style={pageBtnStyle(false)} disabled={page >= totalPages} onClick={() => onMove(totalPages)}>마지막</button>
    </div>
  );
}

export default function BusinessLogTab() {
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [sources, setSources] = useState([]);
  const [actions, setActions] = useState(DEFAULT_ACTION_OPTIONS);
  const [companies, setCompanies] = useState([]);
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchLogs = useCallback(async (targetPage = 1, nextFilters = filters) => {
    setLoading(true);
    const params = new URLSearchParams({
      limit: String(LIMIT),
      offset: String((targetPage - 1) * LIMIT),
    });
    if (nextFilters.source) params.set('source', nextFilters.source);
    if (nextFilters.sourceGroup) params.set('sourceGroup', nextFilters.sourceGroup);
    if (nextFilters.action) params.set('action', nextFilters.action);
    if (nextFilters.actionGroup) params.set('actionGroup', nextFilters.actionGroup);
    if (nextFilters.search.trim()) params.set('search', nextFilters.search.trim());
    if (nextFilters.person.trim()) params.set('person', nextFilters.person.trim());
    if (nextFilters.company) params.set('company', nextFilters.company);
    if (nextFilters.month) params.set('month', nextFilters.month);
    if (nextFilters.dateFrom) params.set('dateFrom', nextFilters.dateFrom);
    if (nextFilters.dateTo) params.set('dateTo', nextFilters.dateTo);

    const res = await apiClient.get(`/api/admin/logs/business?${params.toString()}`);
    setLoading(false);
    if (!res.ok) return;
    setList(res.data?.list || []);
    setTotal(res.data?.total || 0);
    setSources(res.data?.sources || []);
    setCompanies(res.data?.companies || []);
    const serverActions = Array.isArray(res.data?.actions) ? res.data.actions : [];
    setActions([{ value: '', label: '전체' }, ...serverActions.filter((item) => item.value)]);
    setPage(targetPage);
    setExpandedId(null);
  }, [filters]);

  useEffect(() => {
    fetchLogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleFilterChange(key, value) {
    setFilters((prev) => {
      if (key === 'month') return { ...prev, month: value, dateFrom: '', dateTo: '' };
      if (key === 'dateFrom' || key === 'dateTo') return { ...prev, [key]: value, month: '' };
      if (key === 'source') return { ...prev, source: value, sourceGroup: '' };
      if (key === 'action') return { ...prev, action: value, actionGroup: '' };
      return { ...prev, [key]: value };
    });
  }

  function handleQuickFilter(key) {
    const nextFilters = makeQuickFilterFilters(key, filters);
    setFilters(nextFilters);
    fetchLogs(1, nextFilters);
  }

  function handleSearch(e) {
    e.preventDefault();
    fetchLogs(1);
  }

  function handleReset() {
    setFilters(INITIAL_FILTERS);
    fetchLogs(1, INITIAL_FILTERS);
  }

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div>
      <BusinessLogFilters
        filters={filters}
        sources={sources}
        actions={actions}
        companies={companies}
        onChange={handleFilterChange}
        onQuickFilter={handleQuickFilter}
        onSearch={handleSearch}
        onReset={handleReset}
      />

      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
        총 <strong style={{ color: 'var(--text-sub)' }}>{total.toLocaleString()}</strong>건
      </div>

      {loading && <div style={{ ...placeholderStyle, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12 }}>로딩 중...</div>}
      {!loading && list.length === 0 && <div style={{ ...placeholderStyle, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12 }}>업무 이력이 없습니다.</div>}

      {!loading && list.length > 0 && isMobile && (
        <div style={mobileLogListStyle}>
          {list.map((row) => <MobileCard key={row.id} row={row} expanded={expandedId === row.id} onToggle={() => setExpandedId(expandedId === row.id ? null : row.id)} />)}
        </div>
      )}

      {!loading && list.length > 0 && !isMobile && (
        <div style={tableWrapStyle}>
          <table style={tblStyle}>
            <thead>
              <tr>
                <th style={thStyle}>시각</th>
                <th style={thStyle}>메뉴</th>
                <th style={thStyle}>대상</th>
                <th style={thStyle}>처리자</th>
                <th style={thStyle}>행동</th>
                <th style={thStyle}>내용</th>
              </tr>
            </thead>
            <tbody>
              {list.map((row, idx) => (
                <React.Fragment key={row.id}>
                  <tr style={{ ...trStyle(idx), cursor: 'pointer' }} onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 11 }}>{row.created_at_kst?.slice(0, 16)}</td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontWeight: 700, color: 'var(--text)' }}>{row.menu}</td>
                    <td style={{ ...tdStyle, minWidth: 140 }}>{row.target}</td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{row.actor}</td>
                    <td style={tdStyle}><span style={actionBadgeStyle(row.action)}>{row.action_label}</span></td>
                    <td style={{ ...tdStyle, minWidth: 260, color: 'var(--text-sub)' }}>{row.summary}</td>
                  </tr>
                  {expandedId === row.id && (
                    <tr style={trStyle(idx)}>
                      <td style={{ ...tdStyle, paddingTop: 0 }} colSpan={6}><DetailPanel row={row} /></td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination page={page} totalPages={totalPages} onMove={fetchLogs} />
    </div>
  );
}
